import { motion } from 'framer-motion';
import { Dumbbell, Users, Zap, Clock, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const EASE = [0.22, 1, 0.36, 1];

const services = [
  {
    icon: Dumbbell,
    title: 'WOD CrossFit',
    slug: 'crossfit-wod',
    number: '01',
    desc: 'Le Workout Of the Day : mouvements fonctionnels variés à haute intensité, adapté à tous les niveaux.',
    tagline: 'FORCE · ENDURANCE · INTENSITÉ',
  },
  {
    icon: Zap,
    title: 'WOD HYROX',
    slug: 'hyrox',
    number: '02',
    desc: 'Préparation aux compétitions HYROX : course, rameur, sled push/pull, wall balls et plus.',
    tagline: 'COMPÉTITION · HYBRID FITNESS',
  },
  {
    icon: Users,
    title: 'WOD Team CrossFit & HYROX',
    slug: 'team-wod',
    number: '03',
    desc: 'Entraînements en équipe pour développer cohésion, motivation et repousser vos limites ensemble.',
    tagline: 'TEAM · ESPRIT COLLECTIF',
  },
  {
    icon: Clock,
    title: 'Free Access',
    slug: 'free-access',
    number: '04',
    desc: 'Créneaux en accès libre pour vous entraîner en autonomie avec tout l\'équipement de la box.',
    tagline: 'AUTONOMIE · LIBRE ACCÈS',
  },
];

const handleMouseMove = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * 100;
  const y = ((e.clientY - rect.top) / rect.height) * 100;
  card.style.setProperty('--mx', `${x}%`);
  card.style.setProperty('--my', `${y}%`);
};

export const Services = () => {
  return (
    <section
      data-testid="services-section"
      className="section-padding relative bg-background overflow-hidden"
    >
      {/* Decorative background accent */}
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 left-0 w-[500px] h-[500px] bg-primary-glow/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="grid md:grid-cols-5 gap-8 items-end mb-14 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, ease: EASE }}
            className="md:col-span-3"
          >
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
              ━━ Nos programmes
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-4 leading-[0.95]">
              LA PRÉPARATION
              <br />
              PHYSIQUE POUR LA{' '}
              <span className="text-gradient-primary italic font-display inline-block pr-2">
                PERFORMANCE
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="md:col-span-2 text-muted-foreground text-base leading-relaxed md:pb-3"
          >
            Coaching sportif, encadrement professionnel et accessible à tous.
            Chaque programme est pensé pour un objectif précis — choisis celui
            qui te correspond.
          </motion.p>
        </div>

        {/* Service cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service, idx) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-5%' }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.1,
                  ease: EASE,
                }}
              >
                <Link
                  to={`/programmes/${service.slug}`}
                  data-testid={`service-card-${idx}`}
                  onMouseMove={handleMouseMove}
                  className="spotlight group block relative bg-white border border-border rounded-2xl p-7 h-full transition-all duration-500 hover:border-primary/30 hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_hsla(155,75%,18%,0.25)]"
                >
                  {/* Number */}
                  <div className="font-display text-6xl text-accent group-hover:text-primary/20 transition-colors duration-500 leading-none mb-4">
                    {service.number}
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary group-hover:rotate-[-8deg] group-hover:scale-110 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]">
                    <Icon
                      className="h-6 w-6 text-primary group-hover:text-white transition-colors duration-500"
                      strokeWidth={2.2}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="font-heading text-xl text-dark mb-2 tracking-wide">
                    {service.title}
                  </h3>

                  {/* Tagline */}
                  <p className="text-primary text-[10px] font-bold tracking-[0.22em] uppercase mb-3">
                    {service.tagline}
                  </p>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {service.desc}
                  </p>

                  {/* CTA with arrow */}
                  <div className="flex items-center gap-1.5 text-primary font-semibold text-xs tracking-[0.18em] uppercase">
                    <span className="link-underline">Découvrir</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
